import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Plus,
  Trash2,
  Save,
  Edit3,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Eye,
  EyeOff,
  Building2,
  Users,
  Quote,
  FileText,
  HelpCircle,
  Briefcase,
  BookOpen,
  Home,
  Upload,
  Image as ImageIcon,
  Loader2,
  Mail,
  Copy,
  ExternalLink,
  MapPin,
  Handshake,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Reusable Image Upload Component
const ImageUploader = ({ 
  currentImage, 
  currentPublicId, 
  onUpload, 
  onDelete, 
  token,
  label = "Image",
  folder = "images",
  maxWidth = 800,
  maxHeight = 800
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Resize image client-side before upload
  const resizeImage = (file, maxW, maxH) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let { width, height } = img;
          
          // Only resize if larger than max dimensions
          if (width <= maxW && height <= maxH) {
            resolve(file);
            return;
          }
          
          // Calculate new dimensions maintaining aspect ratio
          if (width > height) {
            if (width > maxW) {
              height = Math.round((height * maxW) / width);
              width = maxW;
            }
          } else {
            if (height > maxH) {
              width = Math.round((width * maxH) / height);
              height = maxH;
            }
          }
          
          // Create canvas and resize
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to blob
          canvas.toBlob((blob) => {
            const resizedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            resolve(resizedFile);
          }, 'image/jpeg', 0.9);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    // Validate file size (max 10MB before resize)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    try {
      // Resize image client-side first
      const resizedFile = await resizeImage(file, maxWidth, maxHeight);
      
      // Get signed upload params from backend
      const sigResponse = await axios.get(`${API}/uploads/signature?folder=${folder}`);
      const sig = sigResponse.data;

      // Upload directly to Cloudinary
      const formData = new FormData();
      formData.append('file', resizedFile);
      formData.append('api_key', sig.api_key);
      formData.append('timestamp', sig.timestamp);
      formData.append('signature', sig.signature);
      formData.append('folder', sig.folder);

      const cloudinaryResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        { method: 'POST', body: formData }
      );

      const result = await cloudinaryResponse.json();

      if (result.secure_url) {
        onUpload(result.secure_url, result.public_id);
        toast.success(`${label} uploaded successfully`);
      } else {
        throw new Error(result.error?.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Failed to upload ${label.toLowerCase()}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!currentPublicId) {
      // Just clear the URL if no public_id (old external URL)
      onDelete();
      return;
    }

    try {
      await axios.delete(`${API}/uploads/delete`, {
        data: { public_id: currentPublicId },
        headers: { Authorization: `Bearer ${token}` }
      });
      onDelete();
      toast.success(`${label} deleted`);
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(`Failed to delete ${label.toLowerCase()}`);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleUpload(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium mb-1">{label}</label>
      
      {currentImage ? (
        <div className="relative">
          <div className="border rounded-lg p-4 bg-gray-50">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 bg-white rounded-lg border flex items-center justify-center overflow-hidden">
                <img 
                  src={currentImage} 
                  alt={`${label} preview`}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-600 truncate max-w-[200px]">
                  {currentImage.split('/').pop()}
                </p>
                <div className="flex gap-2 mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    <Upload className="w-3 h-3 mr-1" />
                    Replace
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleDelete}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Remove
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            dragOver 
              ? 'border-gold-500 bg-gold-50' 
              : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          {uploading ? (
            <div className="flex flex-col items-center">
              <Loader2 className="w-8 h-8 text-gold-500 animate-spin" />
              <p className="text-sm text-gray-500 mt-2">Uploading...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <ImageIcon className="w-8 h-8 text-gray-400" />
              <p className="text-sm text-gray-600 mt-2">
                Drop an image here or click to upload
              </p>
              <p className="text-xs text-gray-400 mt-1">
                PNG, JPG, GIF up to 10MB (auto-resized)
              </p>
            </div>
          )}
        </div>
      )}
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => handleUpload(e.target.files[0])}
        className="hidden"
      />
    </div>
  );
};

const ContentManager = ({ isOpen, onClose, token, onContentChange }) => {
  const [activeSection, setActiveSection] = useState('section-headers');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  // Section Headers state
  const [sectionHeaders, setSectionHeaders] = useState({});
  const [headersSaving, setHeadersSaving] = useState(false);
  const [expandedHeaders, setExpandedHeaders] = useState({});
  const [panelVisibility, setPanelVisibility] = useState({});
  const [panelOrder, setPanelOrder] = useState([]);
  
  // Hero content state
  const [heroContent, setHeroContent] = useState({
    headline: "Strategic Solutions for",
    headline_highlight: "Business Excellence",
    description: "",
    badge_text: "Private & Confidential Consulting",
    primary_button: "Schedule Consultation",
    secondary_button: "Our Services",
    stat1_value: "200+",
    stat1_label: "Businesses Helped",
    stat2_value: "$500M+",
    stat2_label: "Capital Secured",
    stat3_value: "25+",
    stat3_label: "Years Experience",
    stat4_value: "95%",
    stat4_label: "Client Satisfaction",
  });
  const [heroSaving, setHeroSaving] = useState(false);

  // Contact content state
  const [contactContent, setContactContent] = useState({
    badge_text: "Get In Touch",
    headline: "Start Your Transformation",
    description: "Schedule a confidential consultation to discuss how we can help your business achieve its full potential.",
    business_hours: "Mon - Fri: 9:00 AM - 6:00 PM\nSat - Sun: By Appointment",
    form_disclaimer: "All information is treated as confidential and will not be shared with third parties.",
    phone: "(214) 673-2710",
    phone_alt: "",
    email: "info@shireadvisors.com",
    address: "516 South I-35E, Denton, TX 76205",
  });
  const [contactSaving, setContactSaving] = useState(false);

  // Custom panels state
  const [customPanels, setCustomPanels] = useState([]);

  const sections = [
    { id: 'section-headers', name: 'Section Headers', icon: Edit3, endpoint: 'section-headers', isSpecial: true },
    { id: 'hero', name: 'Cover Page', icon: Home, endpoint: 'hero', isSpecial: true },
    { id: 'investments', name: 'Investments', icon: Building2, endpoint: 'investments' },
    { id: 'services', name: 'Services', icon: Briefcase, endpoint: 'services' },
    { id: 'team', name: 'Team Members', icon: Users, endpoint: 'team' },
    { id: 'testimonials', name: 'Testimonials', icon: Quote, endpoint: 'testimonials' },
    { id: 'case-studies', name: 'Case Studies', icon: FileText, endpoint: 'case-studies' },
    { id: 'faq', name: 'FAQ', icon: HelpCircle, endpoint: 'faq' },
    { id: 'blog', name: 'Blog Posts', icon: BookOpen, endpoint: 'blog' },
    { id: 'urls-for-sale', name: 'URLs for Sale', icon: ExternalLink, endpoint: 'urls-for-sale' },
    { id: 'real-estate-for-sale', name: 'Property for Sale', icon: MapPin, endpoint: 'real-estate-for-sale' },
    { id: 'converter-partners', name: 'Converter Partners', icon: Handshake, endpoint: 'converter-partners' },
    { id: 'contact', name: 'Contact Page', icon: Mail, endpoint: 'contact', isSpecial: true },
  ];

  // Map custom panel type → section type used for item forms and empty templates
  const customTypeToSectionId = {
    investments: 'investments',
    services: 'services',
    testimonials: 'testimonials',
    'case-studies': 'case-studies',
    faq: 'faq',
    insights: 'blog',
  };

  // Find active custom panel (if any)
  const activeCustomPanel = customPanels.find((p) => p.id === activeSection);
  // Effective section type for form rendering (use custom panel's type mapped)
  const effectiveSectionId = activeCustomPanel
    ? customTypeToSectionId[activeCustomPanel.type] || activeCustomPanel.type
    : activeSection;

  const getEmptyItem = (sectionId) => {
    const templates = {
      investments: {
        name: '',
        website: '',
        website_url: '',
        description: '',
        additional_info: '',
        logo: '',
        logo_public_id: '',
        category: 'Business',
        order: items.length,
        visible: true,
      },
      services: {
        title: '',
        description: '',
        icon: 'Target',
        order: items.length,
        visible: true,
      },
      team: {
        name: '',
        role: '',
        bio: '',
        image: '',
        image_public_id: '',
        order: items.length,
        visible: true,
      },
      testimonials: {
        quote: '',
        author: '',
        company: '',
        rating: 5,
        order: items.length,
        visible: true,
      },
      'case-studies': {
        title: '',
        industry: '',
        challenge: '',
        solution: '',
        result: '',
        image: '',
        order: items.length,
        visible: true,
      },
      faq: {
        question: '',
        answer: '',
        order: items.length,
        visible: true,
      },
      blog: {
        title: '',
        excerpt: '',
        content: '',
        date: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
        category: 'Business',
        image: '',
        order: items.length,
        visible: true,
      },
      'urls-for-sale': {
        name: '',
        description: '',
        status: 'available',
        order: items.length,
        visible: true,
      },
      'real-estate-for-sale': {
        name: '',
        description: '',
        status: 'available',
        price: '',
        location: '',
        photo: '',
        photo_public_id: '',
        album_url: '',
        order: items.length,
        visible: true,
      },
      'converter-partners': {
        name: '',
        url: '',
        location: '',
        description: '',
        order: items.length,
        visible: true,
      },
    };
    return templates[sectionId] || {};
  };

  useEffect(() => {
    if (isOpen) {
      // Fetch custom panels from layout
      fetchCustomPanels();

      if (activeSection === 'section-headers') {
        fetchSectionHeaders();
      } else if (activeSection === 'hero') {
        fetchHeroContent();
      } else if (activeSection === 'contact') {
        fetchContactContent();
      } else {
        fetchItems();
      }
    }
  }, [isOpen, activeSection]);

  const fetchCustomPanels = async () => {
    try {
      const layoutRes = await axios.get(`${API}/settings/layout`);
      if (layoutRes.data?.panels) {
        const custom = layoutRes.data.panels.filter((p) => p.isCustom);
        setCustomPanels(custom);
      }
    } catch (error) {
      console.error('Error fetching custom panels:', error);
    }
  };

  const fetchSectionHeaders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/content/section-headers`);
      if (response.data) {
        setSectionHeaders(response.data);
      }
    } catch (error) {
      console.error('Error fetching section headers:', error);
    } finally {
      setLoading(false);
    }
    
    // Also fetch panel visibility and order
    try {
      const layoutRes = await axios.get(`${API}/settings/layout`);
      if (layoutRes.data?.panels) {
        const visibility = {};
        layoutRes.data.panels.forEach(panel => {
          visibility[panel.id] = panel.visible;
        });
        setPanelVisibility(visibility);
        // Store panels sorted by order
        const sortedPanels = [...layoutRes.data.panels].sort((a, b) => a.order - b.order);
        setPanelOrder(sortedPanels);
      }
    } catch (error) {
      console.error('Error fetching panel visibility:', error);
    }
  };

  const saveSectionHeader = async (sectionId) => {
    setHeadersSaving(true);
    try {
      await axios.put(`${API}/content/section-headers/${sectionId}`, sectionHeaders[sectionId], {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Section header saved');
      if (onContentChange) onContentChange();
    } catch (error) {
      toast.error('Failed to save section header');
      console.error(error);
    } finally {
      setHeadersSaving(false);
    }
  };

  const updateSectionHeader = (sectionId, field, value) => {
    setSectionHeaders(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        [field]: value
      }
    }));
  };

  const fetchHeroContent = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/content/hero`);
      if (response.data) {
        setHeroContent(response.data);
      }
    } catch (error) {
      console.error('Error fetching hero content:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveHeroContent = async () => {
    setHeroSaving(true);
    try {
      await axios.put(`${API}/content/hero`, heroContent, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Cover page updated successfully');
      if (onContentChange) onContentChange();
    } catch (error) {
      toast.error('Failed to save cover page');
    } finally {
      setHeroSaving(false);
    }
  };

  const fetchContactContent = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/content/contact`);
      if (response.data) {
        setContactContent(response.data);
      }
    } catch (error) {
      console.error('Error fetching contact content:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveContactContent = async () => {
    setContactSaving(true);
    try {
      await axios.put(`${API}/content/contact`, contactContent, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Contact page updated successfully');
      if (onContentChange) onContentChange();
    } catch (error) {
      toast.error('Failed to save contact page');
    } finally {
      setContactSaving(false);
    }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      // Check if this is a custom panel
      const customPanel = customPanels.find((p) => p.id === activeSection);
      if (customPanel) {
        const response = await axios.get(`${API}/content/custom-panel/${customPanel.id}`);
        setItems(Array.isArray(response.data) ? response.data : []);
      } else {
        const section = sections.find(s => s.id === activeSection);
        if (!section) {
          setItems([]);
          return;
        }
        const response = await axios.get(`${API}/content/${section.endpoint}`);
        setItems(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveItem = async () => {
    if (!editingItem) return;

    try {
      const customPanel = customPanels.find((p) => p.id === activeSection);

      if (customPanel) {
        // Custom panel item
        if (editingItem.id) {
          await axios.put(
            `${API}/content/custom-panel/${customPanel.id}/items/${editingItem.id}`,
            editingItem,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          toast.success('Item updated successfully');
        } else {
          await axios.post(
            `${API}/content/custom-panel/${customPanel.id}/items`,
            editingItem,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          toast.success('Item created successfully');
        }
      } else {
        const section = sections.find(s => s.id === activeSection);

        if (editingItem.id) {
          await axios.put(
            `${API}/content/${section.endpoint}/${editingItem.id}`,
            editingItem,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          toast.success('Item updated successfully');
        } else {
          await axios.post(
            `${API}/content/${section.endpoint}`,
            editingItem,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          toast.success('Item created successfully');
        }
      }

      setIsDialogOpen(false);
      setEditingItem(null);
      fetchItems();
      if (onContentChange) onContentChange();
    } catch (error) {
      toast.error('Failed to save item');
      console.error(error);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!confirm('Are you sure you want to delete this item?')) return;

    try {
      const customPanel = customPanels.find((p) => p.id === activeSection);
      if (customPanel) {
        await axios.delete(
          `${API}/content/custom-panel/${customPanel.id}/items/${itemId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        const section = sections.find(s => s.id === activeSection);
        await axios.delete(
          `${API}/content/${section.endpoint}/${itemId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      toast.success('Item deleted');
      fetchItems();
      if (onContentChange) onContentChange();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleToggleVisibility = async (item) => {
    try {
      const customPanel = customPanels.find((p) => p.id === activeSection);
      const updated = { ...item, visible: !item.visible };
      if (customPanel) {
        await axios.put(
          `${API}/content/custom-panel/${customPanel.id}/items/${item.id}`,
          updated,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        const section = sections.find(s => s.id === activeSection);
        await axios.put(
          `${API}/content/${section.endpoint}/${item.id}`,
          updated,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      fetchItems();
      if (onContentChange) onContentChange();
    } catch (error) {
      toast.error('Failed to update visibility');
    }
  };

  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState(null);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newItems = [...items];
    const draggedItem = newItems[draggedIndex];
    
    // Remove from old position
    newItems.splice(draggedIndex, 1);
    // Insert at new position
    newItems.splice(index, 0, draggedItem);
    
    // Update order values
    newItems.forEach((item, i) => {
      item.order = i;
    });

    setItems(newItems);
    setDraggedIndex(index);
  };

  const handleDragEnd = async () => {
    if (draggedIndex === null) return;
    
    // Save the new order to backend
    try {
      const section = sections.find(s => s.id === activeSection);
      
      // Update each item's order in the backend
      const updatePromises = items.map((item, index) => 
        axios.put(
          `${API}/content/${section.endpoint}/${item.id}`,
          { ...item, order: index },
          { headers: { Authorization: `Bearer ${token}` } }
        )
      );
      
      await Promise.all(updatePromises);
      toast.success('Order saved');
      if (onContentChange) onContentChange();
    } catch (error) {
      toast.error('Failed to save order');
      fetchItems(); // Revert to original order
    }
    
    setDraggedIndex(null);
  };

  const openEditDialog = (item = null) => {
    setEditingItem(item || getEmptyItem(effectiveSectionId));
    setIsDialogOpen(true);
  };

  const renderItemCard = (item, index) => {
    const displayName = item.name || item.title || item.question || item.author || 'Untitled';
    const displaySubtitle = item.category || item.role || item.company || item.industry || '';
    
    return (
      <div
        key={item.id}
        draggable
        onDragStart={(e) => handleDragStart(e, index)}
        onDragOver={(e) => handleDragOver(e, index)}
        onDragEnd={handleDragEnd}
        className={`flex items-center gap-4 p-4 bg-white rounded-lg border-2 transition-all ${
          draggedIndex === index
            ? 'border-gold-500 bg-gold-50 shadow-lg'
            : item.visible 
              ? 'border-gray-200 hover:shadow-md' 
              : 'border-gray-200 opacity-50'
        }`}
      >
        <div className="cursor-grab active:cursor-grabbing">
          <GripVertical className="w-5 h-5 text-gray-400" />
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-navy-900 truncate">{displayName}</h4>
          {displaySubtitle && (
            <p className="text-sm text-gray-500 truncate">{displaySubtitle}</p>
          )}
        </div>

        <div className="flex items-center gap-2" onMouseDown={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleToggleVisibility(item);
            }}
            className={`p-2 rounded-lg transition-colors ${
              item.visible
                ? 'text-green-600 hover:bg-green-50'
                : 'text-gray-400 hover:bg-gray-100'
            }`}
          >
            {item.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </button>
          
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openEditDialog(item);
            }}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <Edit3 className="w-4 h-4" />
          </button>
          
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDeleteItem(item.id);
            }}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  const renderEditForm = () => {
    if (!editingItem) return null;

    const updateField = (field, value) => {
      setEditingItem(prev => ({ ...prev, [field]: value }));
    };

    switch (effectiveSectionId) {
      case 'investments':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Company Name *</label>
              <Input
                value={editingItem.name || ''}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="Company name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Website Display</label>
                <Input
                  value={editingItem.website || ''}
                  onChange={(e) => updateField('website', e.target.value)}
                  placeholder="www.example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Website URL</label>
                <Input
                  value={editingItem.website_url || ''}
                  onChange={(e) => updateField('website_url', e.target.value)}
                  placeholder="https://www.example.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description *</label>
              <Textarea
                value={editingItem.description || ''}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Company description"
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Additional Info</label>
              <Textarea
                value={editingItem.additional_info || ''}
                onChange={(e) => updateField('additional_info', e.target.value)}
                placeholder="Additional information"
                rows={2}
              />
            </div>
            
            {/* Logo Upload Section */}
            <ImageUploader
              currentImage={editingItem.logo}
              currentPublicId={editingItem.logo_public_id}
              onUpload={(url, publicId) => {
                updateField('logo', url);
                updateField('logo_public_id', publicId);
              }}
              onDelete={() => {
                updateField('logo', '');
                updateField('logo_public_id', '');
              }}
              token={token}
              label="Company Logo"
              folder="logos"
              maxWidth={400}
              maxHeight={400}
            />
            
            <div>
              <label className="block text-sm font-medium mb-1">Category</label>
              <Input
                value={editingItem.category || ''}
                onChange={(e) => updateField('category', e.target.value)}
                placeholder="e.g., Automotive, Energy"
              />
            </div>
          </div>
        );

      case 'services':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Service Title *</label>
              <Input
                value={editingItem.title || ''}
                onChange={(e) => updateField('title', e.target.value)}
                placeholder="Service name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description *</label>
              <Textarea
                value={editingItem.description || ''}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Service description"
                rows={4}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Icon</label>
              <Input
                value={editingItem.icon || ''}
                onChange={(e) => updateField('icon', e.target.value)}
                placeholder="Icon name (e.g., Target, BarChart3)"
              />
              <p className="text-xs text-gray-500 mt-1">
                Available: Target, BarChart3, Wallet, Blocks, Scale, Settings, TrendingUp, Users
              </p>
            </div>
          </div>
        );

      case 'team':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name *</label>
              <Input
                value={editingItem.name || ''}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="Full name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Role *</label>
              <Input
                value={editingItem.role || ''}
                onChange={(e) => updateField('role', e.target.value)}
                placeholder="Job title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Bio *</label>
              <Textarea
                value={editingItem.bio || ''}
                onChange={(e) => updateField('bio', e.target.value)}
                placeholder="Short biography"
                rows={3}
              />
            </div>
            
            {/* Photo Upload Section */}
            <ImageUploader
              currentImage={editingItem.image}
              currentPublicId={editingItem.image_public_id}
              onUpload={(url, publicId) => {
                updateField('image', url);
                updateField('image_public_id', publicId);
              }}
              onDelete={() => {
                updateField('image', '');
                updateField('image_public_id', '');
              }}
              token={token}
              label="Team Member Photo"
              folder="team"
              maxWidth={600}
              maxHeight={600}
            />
          </div>
        );

      case 'testimonials':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Quote *</label>
              <Textarea
                value={editingItem.quote || ''}
                onChange={(e) => updateField('quote', e.target.value)}
                placeholder="Client testimonial"
                rows={4}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Author Name *</label>
                <Input
                  value={editingItem.author || ''}
                  onChange={(e) => updateField('author', e.target.value)}
                  placeholder="Client name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Company *</label>
                <Input
                  value={editingItem.company || ''}
                  onChange={(e) => updateField('company', e.target.value)}
                  placeholder="Company name"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Rating (1-5)</label>
              <Input
                type="number"
                min="1"
                max="5"
                value={editingItem.rating || 5}
                onChange={(e) => updateField('rating', parseInt(e.target.value))}
              />
            </div>
          </div>
        );

      case 'case-studies':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title *</label>
                <Input
                  value={editingItem.title || ''}
                  onChange={(e) => updateField('title', e.target.value)}
                  placeholder="Case study title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Industry *</label>
                <Input
                  value={editingItem.industry || ''}
                  onChange={(e) => updateField('industry', e.target.value)}
                  placeholder="e.g., Manufacturing"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Challenge *</label>
              <Textarea
                value={editingItem.challenge || ''}
                onChange={(e) => updateField('challenge', e.target.value)}
                placeholder="What was the problem?"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Solution *</label>
              <Textarea
                value={editingItem.solution || ''}
                onChange={(e) => updateField('solution', e.target.value)}
                placeholder="How did you solve it?"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Result *</label>
              <Textarea
                value={editingItem.result || ''}
                onChange={(e) => updateField('result', e.target.value)}
                placeholder="What was the outcome?"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Image URL</label>
              <Input
                value={editingItem.image || ''}
                onChange={(e) => updateField('image', e.target.value)}
                placeholder="https://..."
              />
            </div>
          </div>
        );

      case 'faq':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Question *</label>
              <Input
                value={editingItem.question || ''}
                onChange={(e) => updateField('question', e.target.value)}
                placeholder="Frequently asked question"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Answer *</label>
              <Textarea
                value={editingItem.answer || ''}
                onChange={(e) => updateField('answer', e.target.value)}
                placeholder="Answer to the question"
                rows={4}
              />
            </div>
          </div>
        );

      case 'blog':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Title *</label>
              <Input
                value={editingItem.title || ''}
                onChange={(e) => updateField('title', e.target.value)}
                placeholder="Blog post title"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Date</label>
                <Input
                  value={editingItem.date || ''}
                  onChange={(e) => updateField('date', e.target.value)}
                  placeholder="January 1, 2025"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                <Input
                  value={editingItem.category || ''}
                  onChange={(e) => updateField('category', e.target.value)}
                  placeholder="e.g., Business Strategy"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Excerpt *</label>
              <Textarea
                value={editingItem.excerpt || ''}
                onChange={(e) => updateField('excerpt', e.target.value)}
                placeholder="Brief summary"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Image URL</label>
              <Input
                value={editingItem.image || ''}
                onChange={(e) => updateField('image', e.target.value)}
                placeholder="https://..."
              />
            </div>
          </div>
        );

      case 'urls-for-sale':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Domain Name *</label>
              <Input
                value={editingItem.name || ''}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="ExampleDomain.com"
                data-testid="url-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <Textarea
                value={editingItem.description || ''}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Brief description of the potential use or value of this URL"
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select
                value={editingItem.status || 'available'}
                onChange={(e) => updateField('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500"
                data-testid="url-status-select"
              >
                <option value="available">Available</option>
                <option value="sold">Sold</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Order</label>
              <Input
                type="number"
                value={editingItem.order || 0}
                onChange={(e) => updateField('order', parseInt(e.target.value) || 0)}
              />
            </div>
          </div>
        );

      case 'converter-partners':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Shop Name *</label>
              <Input
                value={editingItem.name || ''}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="e.g. EV West"
                data-testid="partner-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Website URL</label>
              <Input
                value={editingItem.url || ''}
                onChange={(e) => updateField('url', e.target.value)}
                placeholder="https://evwest.com"
                data-testid="partner-url-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Location</label>
              <Input
                value={editingItem.location || ''}
                onChange={(e) => updateField('location', e.target.value)}
                placeholder="e.g. San Marcos, CA"
                data-testid="partner-location-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">One-line Description</label>
              <Textarea
                value={editingItem.description || ''}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Brief specialty description — e.g. 'Turnkey EV conversions and full parts catalog.'"
                rows={3}
                data-testid="partner-description-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Order</label>
              <Input
                type="number"
                value={editingItem.order || 0}
                onChange={(e) => updateField('order', parseInt(e.target.value) || 0)}
              />
            </div>
          </div>
        );

      case 'real-estate-for-sale':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Property Name *</label>
              <Input
                value={editingItem.name || ''}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="e.g. Honey Farm Estate Lot 3"
                data-testid="property-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Location</label>
              <Input
                value={editingItem.location || ''}
                onChange={(e) => updateField('location', e.target.value)}
                placeholder="e.g. Denton, TX or full street address"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Price</label>
              <Input
                value={editingItem.price || ''}
                onChange={(e) => updateField('price', e.target.value)}
                placeholder='e.g. $450,000 or "Contact for price"'
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <Textarea
                value={editingItem.description || ''}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Brief details — acreage, bedrooms, highlights, etc."
                rows={3}
              />
            </div>

            {/* Photo Upload */}
            <ImageUploader
              currentImage={editingItem.photo}
              currentPublicId={editingItem.photo_public_id}
              onUpload={(url, publicId) => {
                updateField('photo', url);
                updateField('photo_public_id', publicId);
              }}
              onDelete={() => {
                updateField('photo', '');
                updateField('photo_public_id', '');
              }}
              token={token}
              label="Property Photo"
              folder="properties"
              maxWidth={1200}
              maxHeight={900}
            />

            <div>
              <label className="block text-sm font-medium mb-1">Photo Album URL</label>
              <Input
                value={editingItem.album_url || ''}
                onChange={(e) => updateField('album_url', e.target.value)}
                placeholder="https://photos.app.goo.gl/... or any public album link"
              />
              <p className="text-xs text-gray-500 mt-1">
                Optional. Visitors will see a "View Photo Album" button that opens the album in a modal without leaving the site.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select
                value={editingItem.status || 'available'}
                onChange={(e) => updateField('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500"
                data-testid="property-status-select"
              >
                <option value="available">Available</option>
                <option value="coming_soon">Coming Soon</option>
                <option value="under_contract">Under Contract</option>
                <option value="sold">Sold</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Order</label>
              <Input
                type="number"
                value={editingItem.order || 0}
                onChange={(e) => updateField('order', parseInt(e.target.value) || 0)}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative ml-auto w-full max-w-4xl bg-gray-50 shadow-2xl h-full overflow-hidden flex">
        {/* Sidebar */}
        <div className="w-64 bg-navy-900 p-4 flex flex-col">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white">Content Manager</h2>
            <button onClick={onClose} className="text-white/70 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="space-y-1 flex-1 overflow-y-auto">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                  activeSection === section.id
                    ? 'bg-gold-500 text-navy-900'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
                data-testid={`sidebar-section-${section.id}`}
              >
                <section.icon className="w-5 h-5" />
                <span className="font-medium">{section.name}</span>
              </button>
            ))}

            {customPanels.length > 0 && (
              <>
                <div className="pt-4 pb-2 px-3 text-xs font-semibold uppercase tracking-wider text-white/40">
                  Custom Panels
                </div>
                {customPanels.map((panel) => (
                  <button
                    key={panel.id}
                    onClick={() => setActiveSection(panel.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                      activeSection === panel.id
                        ? 'bg-gold-500 text-navy-900'
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                    }`}
                    data-testid={`sidebar-custom-${panel.id}`}
                  >
                    <Copy className="w-5 h-5" />
                    <span className="font-medium truncate">{panel.name}</span>
                  </button>
                ))}
              </>
            )}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <h3 className="text-xl font-semibold text-navy-900">
              {activeCustomPanel
                ? `${activeCustomPanel.name} (Custom ${activeCustomPanel.type})`
                : sections.find(s => s.id === activeSection)?.name}
            </h3>
            {activeSection !== 'hero' && activeSection !== 'contact' && activeSection !== 'section-headers' && (
              <Button
                onClick={() => openEditDialog()}
                className="bg-gold-500 hover:bg-gold-600 text-navy-900"
                data-testid="content-add-new-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add New
              </Button>
            )}
          </div>

          {/* Items List or Special Forms */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeSection === 'section-headers' ? (
              /* Section Headers Editor */
              <div className="max-w-4xl space-y-3">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-blue-800 text-sm">
                    Edit the label, title, and description that appear at the top of each section on your website. Sections are shown in the same order as Panel Layout.
                  </p>
                </div>

                {(() => {
                  // Map panel IDs to section header configs
                  const sectionConfig = {
                    'hero': { id: 'hero', name: 'Hero/Cover Section', panelId: 'hero', noHeader: true },
                    'services': { id: 'services', name: 'Services', panelId: 'services' },
                    'about': { id: 'about', name: 'About Us & Team', panelId: 'about' },
                    'testimonials': { id: 'testimonials', name: 'Testimonials', panelId: 'testimonials' },
                    'case-studies': { id: 'case_studies', name: 'Case Studies', panelId: 'case-studies' },
                    'investments': { id: 'investments', name: 'Investments & Clients', panelId: 'investments' },
                    'faq': { id: 'faq', name: 'FAQ', panelId: 'faq' },
                    'insights': { id: 'insights', name: 'Blog/Insights', panelId: 'insights' },
                    'contact': { id: 'contact', name: 'Contact Form', panelId: 'contact' },
                  };
                  
                  // Build ordered list from panelOrder, excluding hero (it uses Cover Page editor)
                  const orderedSections = panelOrder
                    .filter(panel => panel.id !== 'hero' && sectionConfig[panel.id])
                    .map(panel => sectionConfig[panel.id]);
                  
                  // Add Footer CTA at the end (always shown)
                  orderedSections.push({ id: 'footer_cta', name: 'Footer Call to Action', panelId: null });
                  
                  return orderedSections.map((section) => {
                    const isExpanded = expandedHeaders[section.id];
                    const isVisible = section.panelId ? panelVisibility[section.panelId] !== false : true;
                    return (
                      <div key={section.id} className={`bg-white border rounded-lg overflow-hidden ${!isVisible ? 'border-gray-300 opacity-60' : 'border-gray-200'}`}>
                        {/* Collapsible Header */}
                        <div 
                          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => setExpandedHeaders(prev => ({ ...prev, [section.id]: !prev[section.id] }))}
                      >
                        <div className="flex items-center gap-3">
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-500" />
                          )}
                          <h4 className="font-semibold text-navy-900">{section.name}</h4>
                          {section.panelId && (
                            isVisible ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                                <Eye className="w-3 h-3" />
                                Visible
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-200 text-gray-600 text-xs font-medium rounded-full">
                                <EyeOff className="w-3 h-3" />
                                Hidden
                              </span>
                            )
                          )}
                        </div>
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            onClick={() => saveSectionHeader(section.id)}
                            disabled={headersSaving}
                            className="bg-gold-500 hover:bg-gold-600 text-navy-900"
                          >
                            <Save className="w-3 h-3 mr-1" />
                            Save
                          </Button>
                        </div>
                      </div>
                      
                      {/* Expandable Content */}
                      {isExpanded && (
                        <div className="px-4 pb-4 pt-2 border-t border-gray-100 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium mb-1 text-gray-700">Label (small uppercase text)</label>
                              <Input
                                value={sectionHeaders[section.id]?.label || ''}
                                onChange={(e) => updateSectionHeader(section.id, 'label', e.target.value)}
                                placeholder="e.g., Our Services"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-1 text-gray-700">Title (main heading)</label>
                              <Input
                                value={sectionHeaders[section.id]?.title || ''}
                                onChange={(e) => updateSectionHeader(section.id, 'title', e.target.value)}
                                placeholder="e.g., Comprehensive Business Solutions"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1 text-gray-700">Description (paragraph below title)</label>
                            <Textarea
                              value={sectionHeaders[section.id]?.description || ''}
                              onChange={(e) => updateSectionHeader(section.id, 'description', e.target.value)}
                              placeholder="Enter the description text that appears below the section title..."
                              rows={4}
                              className="resize-y"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                });
                })()}
              </div>
            ) : activeSection === 'hero' ? (
              /* Hero Content Editor */
              <div className="max-w-2xl space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-blue-800 text-sm">
                    Edit your cover page content below. Changes will appear on the main hero section of your website.
                  </p>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold text-navy-900 border-b pb-2">Headlines</h4>
                  <div>
                    <label className="block text-sm font-medium mb-1">Main Headline</label>
                    <Input
                      value={heroContent.headline}
                      onChange={(e) => setHeroContent(prev => ({ ...prev, headline: e.target.value }))}
                      placeholder="Strategic Solutions for"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Highlighted Text (Gold)</label>
                    <Input
                      value={heroContent.headline_highlight}
                      onChange={(e) => setHeroContent(prev => ({ ...prev, headline_highlight: e.target.value }))}
                      placeholder="Business Excellence"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Badge Text</label>
                    <Input
                      value={heroContent.badge_text}
                      onChange={(e) => setHeroContent(prev => ({ ...prev, badge_text: e.target.value }))}
                      placeholder="Private & Confidential Consulting"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <Textarea
                      value={heroContent.description}
                      onChange={(e) => setHeroContent(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Company description..."
                      rows={3}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold text-navy-900 border-b pb-2">Buttons</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Primary Button</label>
                      <Input
                        value={heroContent.primary_button}
                        onChange={(e) => setHeroContent(prev => ({ ...prev, primary_button: e.target.value }))}
                        placeholder="Schedule Consultation"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Secondary Button</label>
                      <Input
                        value={heroContent.secondary_button}
                        onChange={(e) => setHeroContent(prev => ({ ...prev, secondary_button: e.target.value }))}
                        placeholder="Our Services"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold text-navy-900 border-b pb-2">Statistics</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Stat 1 Value</label>
                      <Input
                        value={heroContent.stat1_value}
                        onChange={(e) => setHeroContent(prev => ({ ...prev, stat1_value: e.target.value }))}
                        placeholder="200+"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Stat 1 Label</label>
                      <Input
                        value={heroContent.stat1_label}
                        onChange={(e) => setHeroContent(prev => ({ ...prev, stat1_label: e.target.value }))}
                        placeholder="Businesses Helped"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Stat 2 Value</label>
                      <Input
                        value={heroContent.stat2_value}
                        onChange={(e) => setHeroContent(prev => ({ ...prev, stat2_value: e.target.value }))}
                        placeholder="$500M+"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Stat 2 Label</label>
                      <Input
                        value={heroContent.stat2_label}
                        onChange={(e) => setHeroContent(prev => ({ ...prev, stat2_label: e.target.value }))}
                        placeholder="Capital Secured"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Stat 3 Value</label>
                      <Input
                        value={heroContent.stat3_value}
                        onChange={(e) => setHeroContent(prev => ({ ...prev, stat3_value: e.target.value }))}
                        placeholder="25+"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Stat 3 Label</label>
                      <Input
                        value={heroContent.stat3_label}
                        onChange={(e) => setHeroContent(prev => ({ ...prev, stat3_label: e.target.value }))}
                        placeholder="Years Experience"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Stat 4 Value</label>
                      <Input
                        value={heroContent.stat4_value}
                        onChange={(e) => setHeroContent(prev => ({ ...prev, stat4_value: e.target.value }))}
                        placeholder="95%"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Stat 4 Label</label>
                      <Input
                        value={heroContent.stat4_label}
                        onChange={(e) => setHeroContent(prev => ({ ...prev, stat4_label: e.target.value }))}
                        placeholder="Client Satisfaction"
                      />
                    </div>
                  </div>
                </div>

                <Button
                  onClick={saveHeroContent}
                  disabled={heroSaving}
                  className="w-full bg-gold-500 hover:bg-gold-600 text-navy-900 font-semibold"
                >
                  {heroSaving ? 'Saving...' : 'Save Cover Page'}
                  <Save className="w-4 h-4 ml-2" />
                </Button>
              </div>
            ) : activeSection === 'contact' ? (
              /* Contact Content Editor */
              <div className="max-w-2xl space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-blue-800 text-sm">
                    Edit your contact page content below. Changes will appear on the contact section of your website.
                  </p>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold text-navy-900 border-b pb-2">Section Header</h4>
                  <div>
                    <label className="block text-sm font-medium mb-1">Badge Text</label>
                    <Input
                      value={contactContent.badge_text}
                      onChange={(e) => setContactContent(prev => ({ ...prev, badge_text: e.target.value }))}
                      placeholder="Get In Touch"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Headline</label>
                    <Input
                      value={contactContent.headline}
                      onChange={(e) => setContactContent(prev => ({ ...prev, headline: e.target.value }))}
                      placeholder="Start Your Transformation"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <Textarea
                      value={contactContent.description}
                      onChange={(e) => setContactContent(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Schedule a confidential consultation..."
                      rows={3}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold text-navy-900 border-b pb-2">Contact Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Primary Phone</label>
                      <Input
                        value={contactContent.phone || ''}
                        onChange={(e) => setContactContent(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="(214) 673-2710"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Alternate Phone</label>
                      <Input
                        value={contactContent.phone_alt || ''}
                        onChange={(e) => setContactContent(prev => ({ ...prev, phone_alt: e.target.value }))}
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Email Address</label>
                    <Input
                      value={contactContent.email || ''}
                      onChange={(e) => setContactContent(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="info@shireadvisors.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Office Address</label>
                    <Input
                      value={contactContent.address || ''}
                      onChange={(e) => setContactContent(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="516 South I-35E, Denton, TX 76205"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold text-navy-900 border-b pb-2">Business Information</h4>
                  <div>
                    <label className="block text-sm font-medium mb-1">Business Hours</label>
                    <Textarea
                      value={contactContent.business_hours}
                      onChange={(e) => setContactContent(prev => ({ ...prev, business_hours: e.target.value }))}
                      placeholder="Mon - Fri: 9:00 AM - 6:00 PM"
                      rows={2}
                    />
                    <p className="text-xs text-gray-500 mt-1">Use new lines for multiple entries</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Form Disclaimer</label>
                    <Textarea
                      value={contactContent.form_disclaimer}
                      onChange={(e) => setContactContent(prev => ({ ...prev, form_disclaimer: e.target.value }))}
                      placeholder="All information is treated as confidential..."
                      rows={2}
                    />
                  </div>
                </div>

                <Button
                  onClick={saveContactContent}
                  disabled={contactSaving}
                  className="w-full bg-gold-500 hover:bg-gold-600 text-navy-900 font-semibold"
                >
                  {contactSaving ? 'Saving...' : 'Save Contact Page'}
                  <Save className="w-4 h-4 ml-2" />
                </Button>
              </div>
            ) : loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="w-8 h-8 border-4 border-gold-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 mb-4">No items yet</p>
                <Button onClick={() => openEditDialog()} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Add your first item
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {items.map((item, index) => renderItemCard(item, index))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingItem?.id ? 'Edit Item' : 'Add New Item'}
            </DialogTitle>
          </DialogHeader>
          
          {renderEditForm()}
          
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveItem}
              className="bg-gold-500 hover:bg-gold-600 text-navy-900"
            >
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContentManager;
